import axios from 'axios';

const BASE_URL = '/api';

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refresh = localStorage.getItem('refresh_token');
      if (refresh) {
        try {
          const { data } = await axios.post(`${BASE_URL}/auth/refresh/`, { refresh });
          localStorage.setItem('access_token', data.access);
          api.defaults.headers.Authorization = `Bearer ${data.access}`;
          original.headers.Authorization = `Bearer ${data.access}`;
          return api(original);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (credentials) => api.post('/auth/login/', credentials),
  me: () => api.get('/employees/me/'),
  changePassword: (data) => api.post('/employees/change-password/', data),
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
export const dashboardAPI = { get: () => api.get('/dashboard/') };

// ─── Firms ────────────────────────────────────────────────────────────────────
export const firmsAPI = {
  list: () => api.get('/firms/'),
  get: (id) => api.get(`/firms/${id}/`),
  create: (data) => api.post('/firms/', data),
  update: (id, data) => api.patch(`/firms/${id}/`, data),
};

// ─── Employees ────────────────────────────────────────────────────────────────
export const employeesAPI = {
  list: (params) => api.get('/employees/', { params }),
  get: (id) => api.get(`/employees/${id}/`),
  create: (data) => api.post('/employees/', data),
  update: (id, data) => api.patch(`/employees/${id}/`, data),
  delete: (id) => api.delete(`/employees/${id}/`),
};

// ─── Clients ──────────────────────────────────────────────────────────────────
export const clientsAPI = {
  list: (params) => api.get('/clients/', { params }),
  get: (id) => api.get(`/clients/${id}/`),
  create: (data) => api.post('/clients/', data),
  update: (id, data) => api.patch(`/clients/${id}/`, data),
  delete: (id) => api.delete(`/clients/${id}/`),
  toggleBillReminders: (id, enabled) => api.patch(`/clients/${id}/toggle-bill-reminders/`, { bill_reminders_enabled: enabled }),
};

// ─── Cases ────────────────────────────────────────────────────────────────────
export const casesAPI = {
  list: (params) => api.get('/cases/', { params }),
  get: (id) => api.get(`/cases/${id}/`),
  create: (data) => api.post('/cases/', data),
  update: (id, data) => api.patch(`/cases/${id}/`, data),
  delete: (id) => api.delete(`/cases/${id}/`),
  summary: (id) => api.get(`/cases/${id}/summary/`),
};

// ─── Tasks ────────────────────────────────────────────────────────────────────
export const tasksAPI = {
  list: (params) => api.get('/tasks/', { params }),
  get: (id) => api.get(`/tasks/${id}/`),
  create: (data) => api.post('/tasks/', data),
  update: (id, data) => api.patch(`/tasks/${id}/`, data),
  delete: (id) => api.delete(`/tasks/${id}/`),
};

// ─── Time Entries ─────────────────────────────────────────────────────────────
export const timeEntriesAPI = {
  list: (params) => api.get('/time-entries/', { params }),
  get: (id) => api.get(`/time-entries/${id}/`),
  create: (data) => api.post('/time-entries/', data),
  update: (id, data) => api.patch(`/time-entries/${id}/`, data),
  delete: (id) => api.delete(`/time-entries/${id}/`),
};

// ─── Documents ────────────────────────────────────────────────────────────────
export const documentsAPI = {
  list: (params) => api.get('/documents/', { params }),
  get: (id) => api.get(`/documents/${id}/`),
  upload: (formData) => api.post('/documents/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/documents/${id}/`),
  generate: (data) => api.post('/documents/generate/', data, { responseType: 'blob' }),
};

// ─── Calendar ─────────────────────────────────────────────────────────────────
export const eventsAPI = {
  list: (params) => api.get('/events/', { params }),
  get: (id) => api.get(`/events/${id}/`),
  create: (data) => api.post('/events/', data),
  update: (id, data) => api.patch(`/events/${id}/`, data),
  delete: (id) => api.delete(`/events/${id}/`),
};

// ─── Trust Accounts ───────────────────────────────────────────────────────────
export const trustAccountsAPI = {
  list: (params) => api.get('/trust-accounts/', { params }),
  get: (id) => api.get(`/trust-accounts/${id}/`),
  create: (data) => api.post('/trust-accounts/', data),
  update: (id, data) => api.patch(`/trust-accounts/${id}/`, data),
};

export const trustTransactionsAPI = {
  list: (params) => api.get('/trust-transactions/', { params }),
  create: (data) => api.post('/trust-transactions/', data),
};

// ─── Invoices ─────────────────────────────────────────────────────────────────
export const invoicesAPI = {
  list: (params) => api.get('/invoices/', { params }),
  get: (id) => api.get(`/invoices/${id}/`),
  create: (data) => api.post('/invoices/', data),
  update: (id, data) => api.patch(`/invoices/${id}/`, data),
  delete: (id) => api.delete(`/invoices/${id}/`),
  send: (id) => api.post(`/invoices/${id}/send/`),
  submitForApproval: (id, approverId) => api.post(`/invoices/${id}/submit-for-approval/`, { approver_id: approverId }),
  approve: (id) => api.post(`/invoices/${id}/approve/`),
  approveAndApplyTrust: (id) => api.post(`/invoices/${id}/approve-and-apply-trust/`),
  archive: (id) => api.post(`/invoices/${id}/archive/`),
  pdf: (id) => api.get(`/invoices/${id}/pdf/`, { responseType: 'blob' }),
  addLineItem: (data) => api.post('/invoice-line-items/', data),
  deleteLineItem: (id) => api.delete(`/invoice-line-items/${id}/`),
};

// ─── Expenses ─────────────────────────────────────────────────────────────────
export const expensesAPI = {
  list: (params) => api.get('/expenses/', { params }),
  get: (id) => api.get(`/expenses/${id}/`),
  create: (data) => api.post('/expenses/', data),
  update: (id, data) => api.patch(`/expenses/${id}/`, data),
  delete: (id) => api.delete(`/expenses/${id}/`),
};

// ─── Notes ────────────────────────────────────────────────────────────────────
export const notesAPI = {
  list: (params) => api.get('/notes/', { params }),
  create: (data) => api.post('/notes/', data),
  update: (id, data) => api.patch(`/notes/${id}/`, data),
  delete: (id) => api.delete(`/notes/${id}/`),
};

// ─── Payments ─────────────────────────────────────────────────────────────────
export const paymentsAPI = {
  list: (params) => api.get('/payments/', { params }),
  create: (data) => api.post('/payments/', data),
};

// ─── Receipts ─────────────────────────────────────────────────────────────────
export const receiptsAPI = {
  list: (params) => api.get('/receipts/', { params }),
  create: (data) => api.post('/receipts/', data),
  pdf: (id) => api.get(`/receipts/${id}/pdf/`, { responseType: 'blob' }),
};

// ─── Reports ──────────────────────────────────────────────────────────────────
export const reportsAPI = { get: (params) => api.get('/reports/', { params }) };

// ─── Document Templates & Categories ─────────────────────────────────────────
export const docTemplatesAPI = {
  list: (params) => api.get('/doc-templates/', { params }),
  get: (id) => api.get(`/doc-templates/${id}/`),
  create: (formData) => api.post('/doc-templates/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.patch(`/doc-templates/${id}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/doc-templates/${id}/`),
  generate: (id, caseId) => api.post(`/doc-templates/${id}/generate/`, { case_id: caseId }, { responseType: 'blob' }),
  downloadRaw: (id) => api.get(`/doc-templates/${id}/download/`, { responseType: 'blob' }),
};

export const docCategoriesAPI = {
  list: () => api.get('/doc-categories/'),
  create: (data) => api.post('/doc-categories/', data),
  update: (id, data) => api.patch(`/doc-categories/${id}/`, data),
  delete: (id) => api.delete(`/doc-categories/${id}/`),
};

// ─── Merge Fields ─────────────────────────────────────────────────────────────
export const mergeFieldsAPI = {
  get: (caseId) => api.get('/merge-fields/', { params: caseId ? { case_id: caseId } : {} }),
};

// ─── Custom Fields ────────────────────────────────────────────────────────────
export const customFieldsAPI = {
  list: (params) => api.get('/custom-fields/', { params }),
  create: (data) => api.post('/custom-fields/', data),
  update: (id, data) => api.patch(`/custom-fields/${id}/`, data),
  delete: (id) => api.delete(`/custom-fields/${id}/`),
};

export const customFieldValuesAPI = {
  list: (params) => api.get('/custom-field-values/', { params }),
  upsert: (data) => api.post('/custom-field-values/', data),
  update: (id, data) => api.patch(`/custom-field-values/${id}/`, data),
};

// ─── Accounting ───────────────────────────────────────────────────────────────
export const accountingAPI = {
  dashboard: () => api.get('/accounting/dashboard/'),
};

// ─── Bill Reminders ───────────────────────────────────────────────────────────
export const billReminderSettingsAPI = {
  get: () => api.get('/settings/bill-reminders/'),
  update: (data) => api.patch('/settings/bill-reminders/', data),
};

export const outstandingBalancesAPI = {
  get: () => api.get('/billing/outstanding-balances/'),
};

export const bankAccountsAPI = {
  list: (params) => api.get('/bank-accounts/', { params }),
  get: (id) => api.get(`/bank-accounts/${id}/`),
  create: (data) => api.post('/bank-accounts/', data),
  update: (id, data) => api.patch(`/bank-accounts/${id}/`, data),
  delete: (id) => api.delete(`/bank-accounts/${id}/`),
};

export const bankTransactionsAPI = {
  list: (params) => api.get('/bank-transactions/', { params }),
  get: (id) => api.get(`/bank-transactions/${id}/`),
  create: (data) => api.post('/bank-transactions/', data),
  update: (id, data) => api.patch(`/bank-transactions/${id}/`, data),
  delete: (id) => api.delete(`/bank-transactions/${id}/`),
};
